
import gsap, { ScrollTrigger } from '~/plugins/gsap';
import throttle from 'lodash/throttle';
import Vue from 'vue';
import { transformLink } from '~/transforms/transformComponents';
import { trackNavigationClick } from '~/utility/tracking';
import { mockContactLink, mockMainMenu, mockServiceMenu, mockTeaserLink } from './mockData';

export default Vue.extend({
  name: 'Header',
  props: {
    navInline: {
      type: Object,
      default: () => ({
        state: false,
      }),
    },
  },
  data() {
    return {
      visible: false,
      isClosing: false,
      service: false,
      windowWidth: null as any,
      throttledResize: null as any,
      mainMenu: Array(),
      serviceHeadline: null as any,
      serviceMenu: Array(),
      contact: null as any,
      teaserHeadline: null as String | any,
      teaser: null as any,
      scrollTrigger: null as any,
      scrollDir: 0 as Number,
      scrollStarted: false as Boolean,
      rootElement: null as any,
      stageElement: null as any,
      headerBG: null as any,
      headerShow: false as Boolean,
      headerMobileMoreOut: false as Boolean,
      headerTotal: null as any,
      headerLogo: null as any,
      headerBase: null as any,
      headerBaseHeight: 0 as number,
      headerSub: null as any,
      headerSubContent: null as any,
      headerSubCategory: null as any,
      headerSubContact: null as any,
      navInlineLogoMove: false as Boolean,
      alerts: [] as Array<any>,
      alertIndicator: true,
      hasActiveFlag: false as Boolean,
      seenFlags: [] as Array<string>,
    };
  },

  mounted() {
    this.throttledResize = throttle(this.resizeMenuEvent, 600);
    this.initResizeEvent();
    setTimeout(() => {
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.initAnimation();
        });
      });
    }, 200);

    // Show / hide alert indicator
    const alertsClosed =
      typeof sessionStorage !== 'undefined' ? JSON.parse(sessionStorage.getItem('alertsClosed') ?? '[]') : [];
    this.alertIndicator = alertsClosed.some((item: any) => item.isPermanent && !item.isShown);

    // Update hasActiveFlag
    this.seenFlags = this.getStoredSeenFlags();
    this.updateHasActiveFlag();
  },

  created() {
    this.$root.$on('setIndicator', () => {
      this.alertIndicator = true;
    });

    // Clear data
    this.mainMenu = [];
    this.serviceHeadline = null;
    this.serviceMenu = [];
    this.contact = null;
    this.teaser = null;

    if (process.env.STORYBOOK_ENABLED) {
      this.serviceMenu = mockServiceMenu;
      this.mainMenu = mockMainMenu;
      this.serviceHeadline = 'Leistungen';
      this.contact = transformLink(mockContactLink, 'Kontakt');
      this.teaser = transformLink(mockTeaserLink, 'Wir schaffen die Kreislaufgesellschaft');
    } else {
      this.alerts = this.$marketSettings?.alerts ?? [];

      this.mainMenu =
        this.$marketSettings?.header_menu?.map((linkItem: any) =>
          transformLink({ label: linkItem.label, url: linkItem.link.cached_url })
        ) ?? [];
      // Leistungen >>>
      this.serviceMenu =
        this.$marketSettings?.service_menu?.map((linkItem: any) => ({
          headline: linkItem.headline,
          nav: linkItem.nav.map((item: any) =>
            transformLink({ label: item.label, url: item.link.cached_url, flag: item.flag })
          ),
        })) ?? [];
      this.serviceHeadline = this.$marketSettings.Service;
      this.contact = transformLink(this.$marketSettings.contact_link, this.$marketSettings.contact);
      this.teaser = transformLink(this.$marketSettings.teaser_link, this.$marketSettings.teaser_link_label);
      this.teaserHeadline = this.$marketSettings.teaser;
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.throttledResize);
  },

  methods: {
    closeLayer() {
      this.toggleBodyModalDataTag(false);
      gsap.config({ nullTargetWarn: false });
      gsap.to(['.b-header__layer .minus_svg__icon path'], {
        drawSVG: '100% 100%',
        ease: 'curveNatural',
        duration: 0.5,
      });
      this.isClosing = true;
      setTimeout(() => {
        this.service = false;
        this.visible = false;
        this.isClosing = false;
      }, 400);

      window.removeEventListener('modalClose', this.closeLayer);
    },

    checkLogoAnimation() {
      this.navInlineLogoMove = true;
      this.initAnimation();
    },

    initAnimation() {
      if (!process.env.STORYBOOK_ENABLED) {
        gsap.config({ nullTargetWarn: false });
        // get root element - not the same on storyblok and storybook!
        this.rootElement = document.querySelector('.page') as HTMLElement;
        if (!this.rootElement) {
          this.rootElement = document.querySelector('#root section') as HTMLElement;
        }

        this.headerTotal = this.$el as HTMLElement;
        this.headerBase = this.$el.querySelector('.b-header') as HTMLElement;
        this.headerLogo = this.$el.querySelector('.b-header__logo') as HTMLElement;
        this.headerSub = this.$el.querySelector('.c-nav-inline') as HTMLElement;
        this.headerSubContent = this.$el.querySelector('.c-nav-inline__content') as HTMLElement;
        this.headerSubCategory = this.$el.querySelector('.c-nav-inline__category') as HTMLElement;
        this.headerSubContact = this.$el.querySelector('.c-nav-inline__contact') as HTMLElement;
        // --- kill scrollTrigger if exists AND reset all other stuff!
        if (this.scrollTrigger) {
          this.scrollTrigger.kill();
          if (this.headerSubCategory) {
            this.headerSubCategory.classList.remove('moveToLogo');
            this.headerSubCategory.classList.remove('hideCategory');
            this.headerSub.classList.remove('hideBorder');
          }
          gsap.killTweensOf([this.headerTotal, this.headerLogo, this.headerSub, this.headerSubCategory]);
          gsap.set([this.headerTotal, this.headerBase, this.headerLogo], { y: 0 });
          this.scrollStarted = false;
          this.headerShow = false;
          this.headerMobileMoreOut = false;
        }
        // --- get heights of base header
        this.headerBaseHeight = this.headerBase.offsetHeight + 1;
        setTimeout(() => {
          this.headerBaseHeight = this.headerBase.offsetHeight + 1;
        }, 100);

        this.scrollTrigger = ScrollTrigger.create({
          trigger: this.rootElement, // trigger the whole page
          onUpdate: self => {
            this.scrollDir = self.direction;
            this.updateOnScroll();
          },
        });
      }
    },

    updateOnScroll() {
      const rootTop = this.rootElement.getBoundingClientRect().top;
      // header show/hide logic paired with background white/transparent
      let headerShow = false;
      let headerMobileMoreOut = false;
      if (this.scrollDir === 1) {
        // --- scroll down
        if (rootTop < -10) {
          headerShow = false;
          if (this.navInlineLogoMove) {
            headerMobileMoreOut = true;
          }
          if (this.navInline.state) {
            gsap.to(this.headerTotal, { backgroundColor: 'white', ease: 'curveNatural', duration: 0 });
          }
        } else {
          headerShow = true;
        }
      } else {
        // --- scroll up
        if (this.navInlineLogoMove) {
          if (rootTop > -25) {
            headerShow = true;
            headerMobileMoreOut = false;
          } else {
            headerShow = false;
          }
        } else {
          headerShow = true;
        }
        // if scrolling up AND stage isn't top out ---> set background to transparent
        if (rootTop > -25) {
          gsap.to(this.headerTotal, { backgroundColor: 'transparent', ease: 'curveNatural', duration: 0 });
        }
      }
      // ---> show or hide header only if state changes or at the beginning
      if (headerShow !== this.headerShow || headerMobileMoreOut !== this.headerMobileMoreOut || !this.scrollStarted) {
        // if moveToLogo ...
        if (this.navInlineLogoMove) {
          // --- move logo - show/hide contact
          let logoVersatz = 0;
          let headerTarget = this.headerBase;
          if (window.innerWidth >= 560) {
            headerTarget = this.headerLogo;
          }
          if (window.innerWidth >= 768) {
            logoVersatz = -8;
          }
          if (window.innerWidth >= 1920) {
            logoVersatz = -12;
          }
          gsap.to(headerTarget, {
            y: () => (headerShow ? 0 : this.headerBaseHeight + logoVersatz),
            duration: () => (headerShow ? 0.6 : 0.4),
            ease: 'curveNatural',
          });
          gsap.to(['.b-header__contact'], {
            autoAlpha: () => (headerShow ? 1 : 0),
            duration: 0.4,
            ease: 'curveNatural',
          });
          // --- move category to logo or reverse
          let categoryMarginTop = 36;
          if (window.innerWidth >= 768) {
            categoryMarginTop = 38;
          }
          if (window.innerWidth >= 1920) {
            categoryMarginTop = 60;
          }
          gsap.to(this.headerSubCategory, {
            marginTop: () => (headerShow ? 0 : categoryMarginTop),
            duration: () => (headerShow ? 0.6 : 0.4),
            ease: 'curveNatural',
          });
          // --- change category font-size and colour
          if (!headerShow) {
            this.headerSubCategory.classList.add('moveToLogo');
            this.headerSub.classList.add('hideBorder');
          } else {
            this.headerSubCategory.classList.remove('moveToLogo');
            setTimeout(() => {
              this.headerSub.classList.remove('hideBorder');
            }, 300);
          }
        }
        // show/hide header
        let moveY = 0;
        if (!headerShow) {
          moveY = -this.headerBaseHeight;
        }
        if (window.innerWidth < 560) {
          if (this.navInline.state && !this.navInlineLogoMove) {
            if (!headerShow) {
              moveY = -this.headerBaseHeight - 20;
            }
          }
          if (this.navInline.state && this.navInlineLogoMove) {
            if (headerMobileMoreOut) {
              moveY = -this.headerBaseHeight - 55;
            }
          }
          if (this.headerSubCategory) {
            if (
              (!this.navInlineLogoMove && this.navInline.state && !headerShow) ||
              (this.navInlineLogoMove && headerMobileMoreOut)
            ) {
              this.headerSubCategory.classList.add('hideCategory');
            } else {
              this.headerSubCategory.classList.remove('hideCategory');
            }
          }
        }
        gsap.to(this.headerTotal, {
          y: () => moveY,
          duration: () => (headerShow ? 0.6 : 0.4),
          ease: 'curveNatural',
        });
        // if state changes from hide to show ---> check background white/transparent
        if (headerShow && !this.headerShow) {
          gsap.delayedCall(0.3, this.checkHeaderBG);
        }
        this.headerShow = headerShow;
        this.headerMobileMoreOut = headerMobileMoreOut;
      }
      this.scrollStarted = true;
    },

    checkHeaderBG() {
      const rootTop = this.rootElement.getBoundingClientRect().top;
      // if rootTop is still on top out ---> white // otherwise transparent
      if (rootTop < -25) {
        gsap.to(this.headerTotal, { backgroundColor: 'white', ease: 'curveNatural', duration: 0 });
      } else {
        gsap.to(this.headerTotal, { backgroundColor: 'transparent', ease: 'curveNatural', duration: 0 });
      }
    },

    initResizeEvent() {
      window.addEventListener('resize', this.throttledResize);
    },

    menuEvent() {
      this.visible = !this.visible;
      if (this.visible) {
        window.addEventListener('modalClose', this.closeLayer, { passive: true });
      }
      if (this.service === true) {
        this.service = false;
      }
    },

    resizeMenuEvent() {
      // update animation if width has changed:
      if (window.innerWidth !== this.windowWidth) {
        this.initAnimation();
      }
      this.windowWidth = window.innerWidth;
      if (this.visible === true) {
        this.visible = false;
      }
      if (this.service === true) {
        this.service = false;
      }
    },

    toggleServiceLayer() {
      this.service = !this.service;
      this.toggleBodyModalDataTag(this.service);
      if (this.service) {
        window.addEventListener('modalClose', this.closeLayer, { passive: true });
        // Track opening of service layer
        if (process.client) {
          trackNavigationClick(
            this.serviceHeadline || 'Leistungen',
            '#service-menu',
            'main-nav'
          );
        }
      }
      this.addFlagsToSeenFlags();
    },

    setHasIndicatorClass() {
      Array.from(document.querySelectorAll('.b-header__nav-item--news')).map((item: any) =>
        item.classList.add('b-header__nav-item--hasIndicator')
      );
    },
    toggleBodyModalDataTag(value: boolean) {
      document.body.dataset.serviceLayerOpen = value.toString();
    },

    getUnseenLinkUid(link: any) {
      return `${link.label}__${link.url}`;
    },

    getStoredSeenFlags() {
      if (typeof localStorage === 'undefined') {
        return [];
      }
      try {
        return JSON.parse(localStorage.getItem('seenFlags') || '[]');
      } catch (e) {
        return [];
      }
    },

    addFlagsToSeenFlags() {
      const newSeenEntries = new Set<string>(this.getStoredSeenFlags());
      this.serviceMenu.forEach(menu => {
        menu.nav.forEach((link: any) => {
          const uid = this.getUnseenLinkUid(link);
          if (link.flag && !newSeenEntries.has(uid)) {
            newSeenEntries.add(uid);
          }
        });
      });
      this.seenFlags = Array.from(newSeenEntries);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('seenFlags', JSON.stringify(this.seenFlags));
      }
      this.updateHasActiveFlag();
    },

    updateHasActiveFlag() {
      let hasFlag = false;
      this.serviceMenu.forEach(menu => {
        menu.nav.forEach((link: any) => {
          const uid = this.getUnseenLinkUid(link);
          if (link.flag && !this.seenFlags.includes(uid)) {
            hasFlag = true;
          }
        });
      });
      this.hasActiveFlag = hasFlag;
    },
  },
});
